import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { ListFooter } from '../ui';

// Risk Sign-offs — a department head's queue of risks they own on submitted Risk
// Analyses and must acknowledge. Acknowledgement is tracked (who/when) and runs
// in PARALLEL with management approval; it does not gate the GPO.

const NAVY = '#2D2C72', INK = '#3B3A8F', RED = '#E22D2D', WARM = '#F7F6F3', VIOLET = '#EEEDF8';
const RATING_LABEL = { low: 'Low', med: 'Medium', high: 'High' };
const RATING_TONE = { low: '#0F7B3F', med: '#854F0B', high: RED };
const CAT_LABEL = {
  commercial: 'Commercial', material_fabric: 'Material — Fabric', material_trims: 'Material — Trims',
  capacity: 'Capacity / Delivery', technical: 'Technical / Quality',
};
const DECISION_LABEL = { proceed: 'Proceed', proceed_with_conditions: 'Proceed with Conditions', hold: 'Hold' };
// A sign-off is only actionable while the RA is still pending_approval; once it is
// approved/held/draft the acknowledge endpoint 409s, so an unsigned row is closed,
// not pending. We keep the row visible (the record it went unsigned) but stop it
// prompting a dead button / inflating the count.
const CLOSED_LABEL = { approved: 'approved', held: 'on hold', draft: 'a draft' };
const isActionable = (it, analysisStatus) => !it.acknowledged_at && analysisStatus === 'pending_approval';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

export default function RiskSignOffs() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);   // analysis_id currently being acknowledged

  const load = useCallback(() => {
    setLoading(true);
    api.get('/risk/sign-offs').then((r) => setItems(r.data.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  // Refresh when a live sign-off request arrives (socket → window event).
  useEffect(() => {
    const h = () => load();
    window.addEventListener('risk:signoff_request', h);
    return () => window.removeEventListener('risk:signoff_request', h);
  }, [load]);

  // Group the flat item list by the analysis it belongs to.
  const groups = [];
  const byId = {};
  items.forEach((it) => {
    if (!byId[it.risk_analysis_id]) {
      byId[it.risk_analysis_id] = {
        id: it.risk_analysis_id, ra_number: it.ra_number, estimate_no: it.estimate_no,
        style_no: it.style_no, brand: it.brand, submitted_by_name: it.submitted_by_name,
        submitted_at: it.submitted_at, analysis_status: it.analysis_status, analysis_decision: it.analysis_decision,
        rows: [],
      };
      groups.push(byId[it.risk_analysis_id]);
    }
    byId[it.risk_analysis_id].rows.push(it);
  });

  const acknowledge = async (analysisId) => {
    setSaving(analysisId);
    try {
      await api.post('/risk/' + analysisId + '/acknowledge', {});
      load();
    } catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
    finally { setSaving(null); }
  };

  // Header/alert count = ACTIONABLE rows only (pending_approval + unacknowledged).
  const pendingCount = items.filter((it) => isActionable(it, it.analysis_status)).length;

  return (
    <div style={{ minHeight: '100vh', background: 'transparent' }}>
      <div style={{ padding: 24 }}>
        <div style={{ marginBottom: 16 }}>
          <h2 style={{ margin: 0, color: NAVY, fontSize: 22 }}>🛡️ Risk Sign-offs</h2>
          <div style={{ color: '#777', fontSize: 13, marginTop: 4 }}>
            Risks you own on submitted Risk Analyses. {pendingCount > 0
              ? <b style={{ color: '#854F0B' }}>{pendingCount} awaiting your sign-off.</b>
              : 'You are all caught up.'}
          </div>
        </div>

        {/* N2 — active, labelled alert (not a silent badge): tells the dept what's blocked. */}
        {!loading && pendingCount > 0 && (
          <div style={{ background: '#FEF3E2', border: '1px solid #F3C77A', borderLeft: '5px solid #E08600', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 22 }}>⚠️</span>
            <div style={{ fontSize: 13, color: '#7A4A00' }}>
              <b>Action needed — {pendingCount} risk sign-off{pendingCount > 1 ? 's' : ''} awaiting your department.</b>{' '}
              Review each Risk Analysis below and sign off your category. <b>The order's GPO cannot be activated until you and the other required departments have signed.</b>
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#aaa' }}>Loading…</div>
        ) : !groups.length ? (
          <div style={{ background: '#fff', border: '1px solid #ececec', borderRadius: 10, padding: 40, textAlign: 'center', color: '#aaa' }}>
            No risk sign-offs assigned to you. 🎉
          </div>
        ) : (<>{groups.map((g) => {
          const myActionable = g.rows.some((it) => isActionable(it, g.analysis_status));
          const anyUnacked = g.rows.some((it) => !it.acknowledged_at);
          const closedUnsigned = anyUnacked && !myActionable;   // unsigned but RA already decided
          return (
            <div key={g.id} style={{ background: '#fff', border: '1px solid #ececec', borderRadius: 10, padding: 18, marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
                <div>
                  <div style={{ fontWeight: 800, color: NAVY, fontSize: 16 }}>{g.ra_number || 'Risk Analysis'}</div>
                  <div style={{ fontSize: 13, color: '#555', marginTop: 2 }}>
                    {g.estimate_no || '—'} · {g.style_no || '—'}{g.brand ? ' · ' + g.brand : ''}
                  </div>
                  <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>
                    Submitted by {g.submitted_by_name || '—'} · {fmtDateTime(g.submitted_at)}
                    {g.analysis_decision && <span> · Management: <b style={{ color: g.analysis_decision === 'hold' ? RED : '#0F7B3F' }}>{DECISION_LABEL[g.analysis_decision]}</b></span>}
                  </div>
                </div>
                <div>
                  {myActionable ? (
                    <button type="button" disabled={saving === g.id} onClick={() => acknowledge(g.id)}
                      style={{ padding: '9px 20px', background: INK, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                      {saving === g.id ? 'Signing…' : '✓ Acknowledge my risk(s)'}
                    </button>
                  ) : closedUnsigned ? (
                    <span style={{ padding: '6px 14px', background: '#F0F0F0', color: '#888', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>Not signed — RA already {CLOSED_LABEL[g.analysis_status] || g.analysis_status}</span>
                  ) : (
                    <span style={{ padding: '6px 14px', background: '#DDF3E4', color: '#0F7B3F', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>✓ Signed off</span>
                  )}
                </div>
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ background: WARM, color: '#666', textAlign: 'left' }}>
                  {['Risk', 'Rating', 'Documents to watch', 'Your sign-off'].map((h) => (
                    <th key={h} style={{ padding: '8px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {g.rows.map((it) => (
                    <tr key={it.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '8px 12px', fontWeight: 600, color: NAVY }}>{CAT_LABEL[it.category] || it.category}</td>
                      <td style={{ padding: '8px 12px', fontWeight: 700, color: RATING_TONE[it.rating] || '#555' }}>{RATING_LABEL[it.rating] || it.rating || '—'}</td>
                      <td style={{ padding: '8px 12px', color: '#888', fontSize: 12 }}>{(Array.isArray(it.mapped_documents) ? it.mapped_documents : []).join(', ') || '—'}</td>
                      <td style={{ padding: '8px 12px' }}>
                        {it.acknowledged_at
                          ? <span style={{ color: '#0F7B3F', fontWeight: 700 }}>✓ {fmtDateTime(it.acknowledged_at)}</span>
                          : g.analysis_status === 'pending_approval'
                            ? <span style={{ color: '#854F0B', fontWeight: 700 }}>⏳ Pending</span>
                            : <span style={{ color: '#999', fontWeight: 600 }}>Not signed — RA already {CLOSED_LABEL[g.analysis_status] || g.analysis_status}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
        <ListFooter shown={groups.length} total={groups.length} noun="sign-offs" />
        </>)}
      </div>
    </div>
  );
}
